import React, { useState } from 'react';
import { useQuickDuel } from '../../context/QuickDuelContext';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSwitch from '../controls/HrnbSwitch';
import '../../css/duels/QuickDuelQueue.css';

/**
 * Компонент очереди быстрых дуэлей с формой настроек
 * Согласно QUICK_DUEL_REACT_INTEGRATION.md
 */
function QuickDuelQueue() {
    const {
        isInQueue,
        queueStatus,
        isLoading,
        error: contextError,
        format,
        roundType,
        estimatedWaitSeconds,
        positionInQueue,
        joinQueue,
        leaveQueue,
    } = useQuickDuel();

    const [selectedFormat, setSelectedFormat] = useState('SOLO');
    const [selectedRoundType, setSelectedRoundType] = useState('SINGLE_ROUND');
    const [localError, setLocalError] = useState(null);

    const FORMAT_OPTIONS = [
        { value: 'SOLO', label: '1v1 Соло' },
        { value: 'TEAM_2V2', label: '2v2 Командный' },
        { value: 'ANY', label: 'Любой формат' },
    ];

    const ROUND_TYPE_OPTIONS = [
        { value: 'SINGLE_ROUND', label: 'Один раунд' },
        { value: 'BEST_OF_THREE', label: 'До двух побед' },
    ];

    const formatWaitTime = (seconds) => {
        if (!seconds) return '—';
        if (seconds < 60) return `${seconds} сек`;
        return `${Math.floor(seconds / 60)} мин`;
    };

    const handleJoin = async () => {
        try {
            setLocalError(null);
            await joinQueue(selectedFormat, selectedRoundType);
        } catch (err) {
            setLocalError(err.response?.data?.message || 'Не удалось войти в очередь');
        }
    };

    const handleLeave = async () => {
        try {
            setLocalError(null);
            await leaveQueue();
        } catch (err) {
            setLocalError(err.response?.data?.message || 'Не удалось выйти из очереди');
        }
    };

    const error = contextError || localError;

    if (error && !isInQueue) {
        return (
            <HrnbCard title="⚠️ Ошибка">
                <div className="quick-duel-queue__error">
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Попробовать снова</button>
                </div>
            </HrnbCard>
        );
    }

    return (
        <HrnbCard title="⚡ Быстрая дуэль">
            <div className="quick-duel-queue">
                {!isInQueue ? (
                    // Форма входа в очередь
                    <div className="quick-duel-queue__form">
                        <div className="quick-duel-queue__form-group">
                            <label>Формат:</label>
                            <select
                                value={selectedFormat}
                                onChange={(e) => setSelectedFormat(e.target.value)}
                                disabled={isLoading}
                            >
                                {FORMAT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="quick-duel-queue__form-group">
                            <label>Тип раундов:</label>
                            <select
                                value={selectedRoundType}
                                onChange={(e) => setSelectedRoundType(e.target.value)}
                                disabled={isLoading}
                            >
                                {ROUND_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <HrnbButton
                            className="quick-duel-queue__btn-join"
                            onClick={handleJoin}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Подключение...' : 'Найти соперника'}
                        </HrnbButton>
                    </div>
                ) : (
                    // Статус ожидания
                    <div className="quick-duel-queue__status">
                        <div className="quick-duel-queue__status-indicator quick-duel-queue__status-indicator--waiting">
                            <span className="quick-duel-queue__pulse"></span>
                            <span>Поиск соперника...</span>
                        </div>

                        <div className="quick-duel-queue__status-info">
                            <p>
                                <strong>Формат:</strong>{' '}
                                {FORMAT_OPTIONS.find(f => f.value === format)?.label}
                            </p>
                            <p>
                                <strong>Ожидание:</strong> ~{formatWaitTime(estimatedWaitSeconds)}
                            </p>
                            {positionInQueue && (
                                <p>
                                    <strong>Позиция в очереди:</strong> #{positionInQueue}
                                </p>
                            )}
                        </div>

                        <HrnbButton
                            className="quick-duel-queue__btn-leave"
                            onClick={handleLeave}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Отмена...' : 'Выйти из очереди'}
                        </HrnbButton>
                    </div>
                )}
            </div>
        </HrnbCard>
    );
}

export default QuickDuelQueue;
